/**
 * OCS Product Card Web Component
 * Lightweight variant of Horizon's product-card.js for carousel product cards.
 *
 * Horizon pattern: section rendering API + DOM morph for variant updates.
 * OCS pattern: client-side value updates only (labels stay server-rendered).
 *
 * This avoids re-rendering the entire carousel section for a single card update.
 *
 * Swatch sort order is handled server-side via CSS `order:` on each swatch button
 * (emitted from ocs-product-card.liquid). No JS sort needed here.
 *
 * @typedef {object} OcsProductCardProps
 * @property {string} data-variant-id - The currently selected variant ID.
 *
 * @typedef {object} OcsProductCardElements
 * @property {HTMLButtonElement[]} swatches - Swatch buttons [data-action="select-variant"].
 * @property {HTMLButtonElement} cartButton - Add to cart button [data-action="add-to-cart"].
 * @property {HTMLInputElement} variantInput - Hidden form input [data-variant-input] — synced on swatch change.
 * @property {HTMLElement} priceValue - Price value element [data-price] b.
 * @property {HTMLElement} priceStriked - Compare-at price element [class*="price-striked"].
 * @property {HTMLElement} saveBadge - Save percentage badge [class*="save-badge"].
 * @property {HTMLSpanElement[]} labels - Hidden label elements [data-label].
 */
if (!customElements.get('ocs-product-card')) {
  // Inside the guard, not above it. Three sections emit this script tag
  // (ocs-home-collection-blocks, ocs-upsell-config, ocs-recently-viewed) and a
  // browser executes every <script src> it is given, so any page carrying two
  // of them runs this file twice. A top-level `const` throws
  // "Identifier has already been declared" on the second run, which aborts the
  // whole script — the guard below is what makes re-execution survivable, and
  // every declaration has to be behind it.
  const OCS_PRODUCT_CARD_PROPERTY_KEYS = [
    '_item_id',
    '_item_category',
    '_item_category2',
    '_item_category3',
    '_item_brand',
    '_ontario_grown',
    '_craft',
    '_price_drop',
    '_new',
    '_item_list_name',
  ];

  class OcsProductCard extends HTMLElement {
    /**
     * Does this card's declared list label match the kind it declares?
     *
     * The second copy of `validateControlledSource`'s table, and deliberately
     * so: the publisher owns the contract but exposes exactly eight public
     * methods (ARCH-001) and a validator is not one of them. Keep the two in
     * step — a kind here that the publisher rejects means the card reports a
     * label the origin token will refuse to carry across a navigation.
     *
     * Liquid emits `data-ocs-origin-kind` and `data-ocs-item-list-name`
     * together or not at all, so a label with no kind is not a surface with no
     * origin — it is a broken one, and fails closed.
     *
     * @param {string|undefined} declaredKind
     * @param {unknown} itemListName
     * @returns {boolean}
     */
    static #listLabelMatchesKind(declaredKind, itemListName) {
      if (typeof itemListName !== 'string') return false;
      const moreItemsPrefix = 'More Items to Consider - ';
      switch (declaredKind) {
        case 'more_items':
          return (
            itemListName.startsWith(moreItemsPrefix) &&
            itemListName.slice(moreItemsPrefix.length).trim().length > 0
          );
        case 'recently_viewed':
          return itemListName === 'Recently viewed';
        default:
          return false;
      }
    }

    /** @type {string | null} */
    #currentVariantId = null;

    /** @type {Record<string, unknown> | null} */
    #selectedAnalyticsMetadata = null;

    #selectedAnalyticsListValid = false;

    #selectedAnalyticsOriginValid = false;

    connectedCallback() {
      this.#currentVariantId = this.getAttribute('data-variant-id');
      this.#selectedAnalyticsMetadata = null;
      this.#selectedAnalyticsListValid = false;
      this.#selectedAnalyticsOriginValid = false;
      const activeSwatch = this.querySelector('.ocs-card--swatch-active');
      if (activeSwatch) this.#synchronizeAnalyticsSnapshot(activeSwatch);
      this.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
    }

    /** @returns {string | null} The currently selected variant ID. */
    get variantId() {
      return this.#currentVariantId;
    }

    /** @param {string} id - The variant ID to set. */
    set variantId(id) {
      this.#currentVariantId = id;
      this.setAttribute('data-variant-id', id);
    }

    /**
     * Single delegated click handler — routes to swatch or add-to-cart.
     * @param {Event} e - The click event.
     */
    handleClick = (e) => {
      if (!(e.target instanceof Element)) return;

      /** @type {HTMLButtonElement | null} */
      const swatch = e.target.closest('[data-action="select-variant"]');
      if (swatch) {
        e.preventDefault();
        e.stopPropagation();
        this.selectVariant(swatch);
        return;
      }

      /** @type {HTMLButtonElement | null} */
      const cartBtn = e.target.closest('[data-action="add-to-cart"]');
      if (cartBtn && !cartBtn.disabled) {
        e.preventDefault();
        e.stopPropagation();
        this.addToCart(cartBtn);
        return;
      }

      // Defect G7. Production's view_item was a click on any product link, not a
      // page view. Deliberately last and deliberately passive: the swatch and
      // cart branches above have already returned, and this one neither
      // preventDefaults nor stopPropagates, so the navigation it reports still
      // happens.
      if (e.target.closest('a[href*="/products/"]')) this.#publishViewItem();
    };

    /**
     * Publish ocs:view_item for the card's currently selected variant — G7.
     */
    #publishViewItem() {
      const analyticsApi = /** @type {any} */ (window).OCSAnalyticsEvents;
      const snapshot = this.#selectedAnalyticsMetadata;
      if (
        !snapshot ||
        !analyticsApi ||
        typeof analyticsApi.createViewItemPayload !== 'function' ||
        typeof analyticsApi.publishOcsAnalyticsEvent !== 'function'
      ) {
        return;
      }

      // A card that declares a controlled origin still has to prove it round
      // tripped, exactly as the add path requires. S19-1's boundary is intact;
      // only the no-origin case is now allowed through.
      if (!this.#selectedAnalyticsOriginValid) return;

      try {
        // The normalized badges Liquid already rendered into the cart property
        // inputs, not a second derivation from compare-at price and variant
        // status. Two derivations could disagree, and the add path validates
        // against these.
        const readProperty = (key, fallback) => {
          const input = this.querySelector(`[name="properties[${key}]"]`);
          const value = input && 'value' in input ? String(/** @type {any} */ (input).value) : '';
          return value || fallback;
        };

        let itemAvailability;
        if (snapshot.variantInStore === 'true') itemAvailability = 'In-Store Only';
        else if (snapshot.variantAvailable === 'false') itemAvailability = 'Out of Stock';
        else if (snapshot.variantAvailable === 'true') itemAvailability = 'In-Stock';

        const payload = analyticsApi.createViewItemPayload(
          {
            // Everything the cart properties already carry comes from them. The
            // swatch dataset holds raw values ("true", a variant status string)
            // that setCartLineProperties has already normalized once; reading
            // the raw side here would be a second normalization that could
            // disagree with the one the add path validates against.
            itemId: readProperty('_item_id', snapshot.itemId),
            brand: readProperty('_item_brand', snapshot.brand),
            category: readProperty('_item_category', snapshot.category),
            category2: readProperty('_item_category2', snapshot.category2),
            category3: readProperty('_item_category3', snapshot.category3),
            ontarioGrown: readProperty('_ontario_grown', snapshot.ontarioGrown),
            craft: readProperty('_craft', snapshot.craft),
            priceDrop: readProperty('_price_drop', snapshot.priceDrop),
            new: readProperty('_new', snapshot.new),
            // Not cart properties: these three exist only on the view event.
            itemName: snapshot.itemName,
            itemVariant: snapshot.itemVariant,
            priceCents: snapshot.priceCents,
            itemAvailability,
            index:
              Number.isInteger(Number(this.dataset.ocsItemIndex)) &&
              Number(this.dataset.ocsItemIndex) > 0
                ? Number(this.dataset.ocsItemIndex)
                : undefined,
            // An absent origin renders as the empty string, which the builder
            // would reject as a malformed label rather than read as absence.
            itemListName: readProperty('_item_list_name', snapshot.itemListName) || undefined,
          },
          this.#analyticsLocation()
        );
        if (!payload) return;

        analyticsApi.publishOcsAnalyticsEvent('ocs:view_item', payload);
      } catch (_) {}
    }

    /**
     * The GA4 `location` this card's events report — G6/G7.
     *
     * Rendered by Liquid rather than hardcoded, so one component reports
     * correctly on every surface it appears on. An unset attribute means PDP,
     * which is what every caller sent before the attribute existed. An
     * off-whitelist value is rejected by the publisher, not here, so there is
     * exactly one place that decides what a location may be.
     *
     * @returns {string}
     */
    #analyticsLocation() {
      return this.dataset.ocsLocation || 'PDP';
    }

    /**
     * Synchronize one authoritative selected-variant analytics snapshot and its
     * hidden cart properties.
     * @param {HTMLButtonElement} swatch - The authoritative active swatch.
     */
    #synchronizeAnalyticsSnapshot(swatch) {
      const rawAnalyticsMetadata = {
        itemId: swatch.dataset.itemId,
        itemName: swatch.dataset.itemName,
        brand: swatch.dataset.itemBrand,
        itemVariant: swatch.dataset.itemVariant,
        category: swatch.dataset.itemCategory,
        category2: swatch.dataset.itemCategory2,
        category3: swatch.dataset.itemCategory3,
        ontarioGrown: swatch.dataset.ontarioGrown,
        craft: swatch.dataset.craft,
        priceCents: swatch.dataset.variantPrice,
        compareAtCents: swatch.dataset.variantComparePrice,
        variantStatus: swatch.dataset.variantStatus,
        variantAvailable: swatch.dataset.variantAvailable,
        // Read only by the view_item builder (G7); the add path has no
        // item_availability parameter. Kept out of every add-path validity
        // check for the same reason the account line keeps it out of its
        // required-keys list: a missing attribute must not be able to take
        // add_to_cart down with it.
        variantInStore: swatch.dataset.variantInStore,
        itemListName: swatch.dataset.itemListName,
      };
      this.#selectedAnalyticsMetadata = rawAnalyticsMetadata;
      const listPairMatches = OcsProductCard.#listLabelMatchesKind(
        this.dataset.ocsOriginKind,
        rawAnalyticsMetadata.itemListName,
      );
      this.#selectedAnalyticsOriginValid =
        rawAnalyticsMetadata.itemListName === undefined || listPairMatches;
      const analyticsApi = /** @type {any} */ (window).OCSAnalyticsEvents;
      const normalizedProperties = new FormData();
      let persistenceSucceeded = false;
      if (analyticsApi && typeof analyticsApi.setCartLineProperties === 'function') {
        const persistenceMetadata = listPairMatches
          ? rawAnalyticsMetadata
          : { ...rawAnalyticsMetadata, itemListName: undefined };
        try {
          persistenceSucceeded = analyticsApi.setCartLineProperties(normalizedProperties, persistenceMetadata);
        } catch (_) {}
      }
      // Defect G6. A surface that declares no list label is not a broken
      // controlled origin — it is a surface with no origin, which the payload
      // builders already accept (item_list_name is optional there). Treating
      // absence as invalid suppressed the whole add_to_cart event on every card
      // outside the PDP upsell carousel, which is why the homepage reported no
      // GA4 add_to_cart at all.
      //
      // S19-1's boundary is unchanged: a *declared* label still has to match
      // the kind the surface declares and still has to round-trip through
      // persistence, and no surface gains a label it did not already have.
      this.#selectedAnalyticsListValid =
        persistenceSucceeded &&
        (rawAnalyticsMetadata.itemListName === undefined ||
          (listPairMatches &&
            normalizedProperties.get('properties[_item_list_name]') === rawAnalyticsMetadata.itemListName));

      for (const key of OCS_PRODUCT_CARD_PROPERTY_KEYS) {
        const input = this.querySelector(`[name="properties[${key}]"]`);
        if (input && 'value' in input) {
          input.value = normalizedProperties.get(`properties[${key}]`) || '';
        }
      }
    }

    /**
     * Swatch selection — updates active state, price values, and cart button.
     * @param {HTMLButtonElement} swatch - The swatch button that was clicked.
     */
    selectVariant(swatch) {
      // Update active swatch
      const container = swatch.closest('[data-swatches]');
      if (container) {
        container.querySelectorAll('[data-action="select-variant"]')
          .forEach((s) => s.classList.remove('ocs-card--swatch-active'));
      }
      swatch.classList.add('ocs-card--swatch-active');

      // Read variant data
      const variantId = swatch.dataset.variantId || '';
      const price = parseInt(swatch.dataset.variantPrice || '0', 10);
      const priceFormatted = swatch.dataset.variantPriceFormatted || '';
      const rawCompare = swatch.dataset.variantComparePrice;
      const comparePrice = rawCompare ? parseInt(rawCompare, 10) : null;
      const comparePriceFormatted = swatch.dataset.variantComparePriceFormatted || '';
      const hasDiscount = !!(comparePrice && comparePrice > price);
      const isAvailable = swatch.dataset.variantAvailable === 'true';
      const isInStore = swatch.dataset.variantInStore === 'true';
      const variantPpu = swatch.dataset.variantPpu || '';
      const variantUom = swatch.dataset.variantUom || '';

      this.variantId = variantId;

      // Sync the form's hidden inputs so FormData uses the selected variant metadata.
      const variantInput = this.querySelector('[data-variant-input]');
      if (variantInput && 'value' in variantInput) variantInput.value = variantId;

      this.#synchronizeAnalyticsSnapshot(swatch);

      // Update price value (keep label markup from Liquid)
      /** @type {HTMLElement | null} */
      const priceB = this.querySelector('[data-price] b');
      if (priceB) {
        priceB.textContent = priceFormatted;
        priceB.classList.toggle('ocs-card--price-sale', hasDiscount);
      }

      // Toggle compare price visibility
      /** @type {HTMLElement | null} */
      const striked = this.querySelector('[class*="price-striked"]');
      if (striked) {
        if (hasDiscount && comparePriceFormatted) {
          striked.textContent = comparePriceFormatted;
          striked.style.display = '';
        } else {
          striked.textContent = '';
          striked.style.display = 'none';
        }
      }

      // Update save badge using server-rendered template
      /** @type {HTMLElement | null} */
      const saveBadge = this.querySelector('[class*="save-badge"]');
      if (saveBadge) {
        if (hasDiscount && comparePrice) {
          const percent = Math.round(((comparePrice - price) / comparePrice) * 100);
          const template = saveBadge.dataset.saveTemplate;
          saveBadge.textContent = template ? template.replace('__PERCENT__', String(percent)) : `Save ${percent}%`;
          saveBadge.style.display = '';
          saveBadge.closest('p')?.removeAttribute('hidden');
        } else {
          saveBadge.style.display = 'none';
          const container = saveBadge.closest('p');
          if (container && !container.querySelector('[class*="ppu"]')) {
            container.setAttribute('hidden', '');
          }
        }
      }

      // Update top-of-card price-drop badge
      const priceDropBadge = this.querySelector('.ocs-card--badge-pricedrop');
      if (priceDropBadge) {
        priceDropBadge.style.display = hasDiscount ? '' : 'none';
        const badgesContainer = priceDropBadge.closest('.ocs-card--badges');
        if (badgesContainer) {
          const hasNewBadge = !!badgesContainer.querySelector('.ocs-card--badge-new');
          badgesContainer.style.display = hasDiscount || hasNewBadge ? '' : 'none';
        }
      }

      // Update cart button state using Liquid-rendered labels
      /** @type {HTMLButtonElement | null} */
      const btn = this.querySelector('[data-action="add-to-cart"]');
      if (btn) {
        /** @type {HTMLElement | null} */
        const textEl = btn.querySelector('[class*="cart-button-text"]');
        const state = isInStore ? 'in-store' : !isAvailable ? 'sold-out' : 'add-to-cart';
        /** @type {HTMLElement | null} */
        const label = this.querySelector(`[data-label="${state}"]`);
        const labelText = label?.textContent?.trim() || '';

        btn.dataset.variantId = variantId;
        btn.classList.remove(
          'ocs-card--cart-button__in-store',
          'ocs-card--cart-button__oos',
          'ocs-button--primary',
          'ocs-button--solid-grey'
        );
        btn.removeAttribute('data-state');
        btn.disabled = false;

        if (textEl) {
          if (labelText) textEl.textContent = labelText;
          textEl.style.visibility = '';
          textEl.style.opacity = '';
        }
        if (labelText) btn.setAttribute('aria-label', labelText);

        if (isInStore) {
          btn.classList.add('ocs-button--solid-grey', 'ocs-card--cart-button__in-store');
          btn.setAttribute('data-state', 'in-store');
          btn.disabled = true;
        } else if (!isAvailable) {
          btn.classList.add('ocs-button--solid-grey', 'ocs-card--cart-button__oos');
          btn.setAttribute('data-state', 'sold-out');
          btn.disabled = true;
        } else {
          btn.classList.add('ocs-button--primary');
          this.ensureCartButtonSpinner(btn);
        }
      }

      // Update PPU display
      const ppuEl = this.querySelector('[class*="--ppu"]');
      const metaLine = ppuEl?.closest('[class*="--meta-line"]');
      if (ppuEl) {
        const hasPpu = !isInStore && variantPpu && variantUom;
        if (hasPpu) {
          ppuEl.textContent = `(${variantPpu} / ${variantUom})`;
          ppuEl.style.display = '';
          metaLine?.removeAttribute('hidden');
        } else {
          ppuEl.textContent = '';
          ppuEl.style.display = 'none';
          if (metaLine) {
            const saveBadgeVisible = !!metaLine.querySelector('[class*="save-badge"]:not([style*="display: none"]):not([style*="display:none"])');
            if (!saveBadgeVisible) metaLine.setAttribute('hidden', '');
          }
        }
      }
    }

    /**
     * Ensure PDP-style spinner markup exists. Buttons that initially render as
     * sold-out/in-store omit the spinner, then swatches can make them available.
     * @param {HTMLButtonElement} btn - The add to cart button.
     */
    ensureCartButtonSpinner(btn) {
      let spinner = btn.querySelector('.ocs-card--cart-button-spinner');
      const spinnerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="50.26" stroke-dashoffset="25.13" /></svg>';

      if (!spinner) {
        btn.insertAdjacentHTML('beforeend', `<span class="ocs-card--cart-button-spinner" data-spinner>${spinnerHTML}</span>`);
        spinner = btn.querySelector('.ocs-card--cart-button-spinner');
      }

      if (spinner && !spinner.querySelector('svg')) {
        spinner.innerHTML = spinnerHTML;
      }
    }

    /**
     * Add to cart via fetch API.
     * @param {HTMLButtonElement} btn - The add to cart button.
     */
    async addToCart(btn) {
      const variantId = this.variantId || btn.dataset.variantId;
      const form = this.querySelector('form');
      if (!variantId || !form) return;

      /** @type {HTMLElement | null} */
      const textEl = btn.querySelector('[class*="cart-button-text"]');
      const originalText = textEl?.textContent || '';
      let hasError = false;

      this.ensureCartButtonSpinner(btn);
      if (textEl) {
        textEl.style.visibility = '';
        textEl.style.opacity = '';
      }

      const isUnavailableState = () => {
        const state = btn.getAttribute('data-state');
        return (
          state === 'sold-out' ||
          state === 'in-store' ||
          btn.classList.contains('ocs-card--cart-button__oos') ||
          btn.classList.contains('ocs-card--cart-button__in-store')
        );
      };

      btn.disabled = true;
      btn.classList.add('ocs-card--cart-button__loading');

      try {
        const formData = new FormData(form);
        formData.set('id', variantId);
        const quantity = formData.get('quantity') || '1';
        const itemCount = parseInt(String(quantity), 10) || 1;
        const analyticsApi = /** @type {any} */ (window).OCSAnalyticsEvents;
        let analyticsPayload = null;
        if (
          this.#selectedAnalyticsMetadata &&
          analyticsApi &&
          typeof analyticsApi.setCartLineProperties === 'function' &&
          typeof analyticsApi.createAddToCartPayload === 'function' &&
          typeof analyticsApi.publishOcsAnalyticsEvent === 'function'
        ) {
          const rawItem = {
            ...this.#selectedAnalyticsMetadata,
            itemAvailability:
              this.#selectedAnalyticsMetadata.variantInStore === 'true'
                ? 'In-Store Only'
                : this.#selectedAnalyticsMetadata.variantAvailable === 'false'
                  ? 'Out of Stock'
                  : this.#selectedAnalyticsMetadata.variantAvailable === 'true'
                    ? 'In-Stock'
                    : '',
            quantity,
          };
          const persistenceItem = this.#selectedAnalyticsListValid
            ? rawItem
            : { ...rawItem, itemListName: undefined };
          try {
            if (typeof formData.delete === 'function') {
              for (const key of OCS_PRODUCT_CARD_PROPERTY_KEYS) {
                formData.delete(`properties[${key}]`);
              }
            }
            analyticsApi.setCartLineProperties(formData, persistenceItem);
            const builtPayload = analyticsApi.createAddToCartPayload([rawItem], this.#analyticsLocation());
            analyticsPayload = this.#selectedAnalyticsOriginValid ? builtPayload : null;
          } catch (_) {
            analyticsPayload = null;
          }
        }

        const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId);
        if (sectionIds.length) {
          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
        }

        const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          body: formData,
        });
        const data = await res.json();

        if (res.ok) {
          if (analyticsPayload) {
            try {
              analyticsApi.publishOcsAnalyticsEvent('ocs:add_to_cart', analyticsPayload);
            } catch (_) {}
          }

          let lineItem = data;

          // cart/add.js may return an incomplete payload on some stores/apps.
          // Fall back to /cart.js so toast/drawer feedback receives a complete line item.
          if (!lineItem || typeof lineItem.price !== 'number' || !lineItem.title) {
            try {
              const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
              const cart = await cartRes.json();
              const selectedVariantId = Number(variantId);
              lineItem = cart.items?.find((item) => item.variant_id === selectedVariantId) || lineItem;
            } catch (_) {
              // fallback failed, keep the cart/add.js payload
            }
          }

          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: lineItem,
              sourceId: `ocs-product-card-${variantId}`,
              data: {
                source: 'product-form-component',
                itemCount,
                variantId,
                sections: data?.sections || {},
              },
            },
          }));

          let toastHandled = false;
          const toast = document.querySelector('ocs-cart-toast-component');
          if (
            toast &&
            typeof toast.showForItem === 'function' &&
            lineItem &&
            typeof lineItem.price === 'number' &&
            lineItem.title
          ) {
            toast.showForItem(lineItem);
            toastHandled = true;
          }

          if (!toastHandled) {
            /** @type {HTMLElement & { open?: () => void; showDialog?: () => void } | null} */
            const drawer = document.querySelector('ocs-cart-drawer-component, cart-drawer-component');
            if (drawer) {
              if (typeof drawer.open === 'function') drawer.open();
              else if (typeof drawer.showDialog === 'function') drawer.showDialog();
            }
          }

        } else {
          hasError = true;
        }
      } catch (err) {
        console.error('Add to cart error:', err);
        hasError = true;
      } finally {
        btn.classList.remove('ocs-card--cart-button__loading');
        if (textEl) {
          textEl.style.visibility = '';
          textEl.style.opacity = '';
        }
        if (!isUnavailableState()) btn.disabled = false;

        if (hasError && !isUnavailableState()) {
          /** @type {HTMLElement | null} */
          const errLabel = this.querySelector('[data-label="error"]');
          btn.classList.add('ocs-card--cart-button__error');
          if (textEl) {
            textEl.textContent = errLabel?.textContent?.trim() || originalText;
            textEl.style.visibility = '';
            textEl.style.opacity = '';
          }
          setTimeout(() => {
            btn.classList.remove('ocs-card--cart-button__error');
            if (textEl && !isUnavailableState()) textEl.textContent = originalText;
          }, 2000);
        }
      }
    }
  }

  customElements.define('ocs-product-card', OcsProductCard);
}
